import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { formatCurrency } from '../../../lib/format';
import Breadcrumb from '../../../components/ui/Breadcrumb';
import Spinner from '../../../components/ui/Spinner';
import {
  preAuthorizationService,
  type PreAuthorization,
  type ApprovePreAuthRequest,
} from '../../../services/preAuthorizationService';
import ConfirmModal from '../../../components/common/ConfirmModal';
import { PRE_AUTH_STATUS_LABELS, PRE_AUTH_STATUS_BADGE } from '../../../domain/statuses';
import { parseApiDate } from '../../../lib/format';

const TYPE_LABELS: Record<string, string> = {
  procedure: 'Procedimiento',
  lab: 'Laboratorio',
  imaging: 'Imagen/Radiología',
  medication: 'Medicamento',
  hospitalization: 'Hospitalización',
  consultation: 'Consulta',
};

const PreAuthorizationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [preAuth, setPreAuth] = useState<PreAuthorization | null>(null);
  const [loading, setLoading] = useState(true);

  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showMarkUsedConfirm, setShowMarkUsedConfirm] = useState(false);
  const [approveForm, setApproveForm] = useState<ApprovePreAuthRequest>({});
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const [externalCode, setExternalCode] = useState('');
  const [savingCode, setSavingCode] = useState(false);
  const [codeSaved, setCodeSaved] = useState(false);

  const fetchData = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await preAuthorizationService.getById(Number(id));
      setPreAuth(data);
      setExternalCode(data.external_auth_code ?? '');
    } catch {
      setPreAuth(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [id]);

  const formatDate = (d: string | null) => {
    if (!d) return '-';
    return (parseApiDate(d) ?? new Date(NaN)).toLocaleDateString('es-DO', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  const formatAmount = (a: string | null) => {
    if (!a) return '-';
    return formatCurrency(a);
  };

  const handleApprove = async () => {
    if (!preAuth) return;
    setActionLoading(true); setActionError(null);
    try {
      await preAuthorizationService.approve(preAuth.id, approveForm);
      setShowApproveModal(false);
      await fetchData();
    } catch {
      setActionError('Error al aprobar la pre-autorización');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!preAuth || !rejectReason.trim()) { setActionError('Ingrese el motivo de rechazo'); return; }
    setActionLoading(true); setActionError(null);
    try {
      await preAuthorizationService.reject(preAuth.id, rejectReason);
      setShowRejectModal(false);
      await fetchData();
    } catch {
      setActionError('Error al rechazar la pre-autorización');
    } finally {
      setActionLoading(false);
    }
  };

  const requestCancel = () => {
    setShowCancelConfirm(true);
    setActionError(null);
  };

  const cancelCancelRequest = () => {
    if (actionLoading) return;
    setShowCancelConfirm(false);
  };

  const handleCancel = async () => {
    if (!preAuth) return;
    setActionLoading(true);
    try {
      await preAuthorizationService.cancel(preAuth.id);
      setShowCancelConfirm(false);
      await fetchData();
    } catch {
      setActionError('Error al cancelar');
    } finally {
      setActionLoading(false);
    }
  };

  const requestMarkUsed = () => {
    setShowMarkUsedConfirm(true);
    setActionError(null);
  };

  const cancelMarkUsedRequest = () => {
    if (actionLoading) return;
    setShowMarkUsedConfirm(false);
  };

  const handleMarkUsed = async () => {
    if (!preAuth) return;
    setActionLoading(true);
    try {
      await preAuthorizationService.markAsUsed(preAuth.id);
      setShowMarkUsedConfirm(false);
      await fetchData();
    } catch {
      setActionError('Error al marcar como usada');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveExternalCode = async () => {
    if (!preAuth) return;
    setSavingCode(true);
    setCodeSaved(false);
    try {
      await preAuthorizationService.updateExternalCode(preAuth.id, externalCode || null);
      setCodeSaved(true);
      setTimeout(() => setCodeSaved(false), 3000);
      await fetchData();
    } catch {
      setActionError('Error al guardar el código ARS');
    } finally {
      setSavingCode(false);
    }
  };

  if (loading) return <Spinner variant="full-page" text="Cargando..." size="lg" />;
  if (!preAuth) return <div className="alert alert-danger">Pre-autorización no encontrada</div>;

  return (
    <>
      <div className="page-header">
        <div className="row align-items-center">
          <div className="col-sm-12">
            <Breadcrumb items={[{ label: 'Inicio', to: '/' }, { label: 'Pre-Autorizaciones', to: '/pre-authorizations' }, { label: preAuth.authorization_number }]} />
          </div>
        </div>
      </div>

      {actionError && <div className="alert alert-danger alert-dismissible">
        {actionError}
        <button type="button" className="btn-close" onClick={() => setActionError(null)} />
      </div>}

      <div className="row">
        <div className="col-md-8">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start mb-4">
                <div>
                  <h4 className="mb-1">{preAuth.authorization_number}</h4>
                  <p className="text-muted mb-0">{TYPE_LABELS[preAuth.authorization_type] ?? preAuth.authorization_type}</p>
                </div>
                <span className={`${PRE_AUTH_STATUS_BADGE[preAuth.status]} fs-6 px-3 py-2`}>{PRE_AUTH_STATUS_LABELS[preAuth.status]}</span>
              </div>

              <div className="row g-3">
                <div className="col-md-6">
                  <label className="text-muted small">Paciente</label>
                  <p className="fw-medium mb-0">
                    {preAuth.patient ? (
                      <Link to={`/patients/${preAuth.patient_id}`}>
                        {preAuth.patient.first_name} {preAuth.patient.last_name}
                      </Link>
                    ) : '-'}
                  </p>
                </div>
                <div className="col-md-6">
                  <label className="text-muted small">Aseguradora / Plan</label>
                  <p className="fw-medium mb-0">
                    {preAuth.patientInsurance?.company?.name ?? '-'}{' '}
                    {preAuth.patientInsurance?.plan?.name ? `— ${preAuth.patientInsurance.plan.name}` : ''}
                  </p>
                  {preAuth.patientInsurance?.policy_number && (
                    <small className="text-muted">Póliza: {preAuth.patientInsurance.policy_number}</small>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="text-muted small">Servicio</label>
                  <p className="fw-medium mb-0">{preAuth.service_name}</p>
                  <small className="text-muted">Cantidad: {preAuth.quantity}</small>
                </div>
                <div className="col-md-6">
                  <label className="text-muted small">Médico Solicitante</label>
                  <p className="fw-medium mb-0">
                    {preAuth.requestedBy ? `Dr. ${preAuth.requestedBy.first_name} ${preAuth.requestedBy.last_name}` : '-'}
                  </p>
                </div>
                <div className="col-md-6">
                  <label className="text-muted small">Fecha de Solicitud</label>
                  <p className="fw-medium mb-0">{formatDate(preAuth.request_date)}</p>
                </div>
                {preAuth.authorization_date && (
                  <div className="col-md-6">
                    <label className="text-muted small">Fecha de Resolución</label>
                    <p className="fw-medium mb-0">{formatDate(preAuth.authorization_date)}</p>
                  </div>
                )}
                {preAuth.notes && (
                  <div className="col-12">
                    <label className="text-muted small">Notas</label>
                    <p className="mb-0">{preAuth.notes}</p>
                  </div>
                )}
              </div>

              {/* Approved financial info */}
              {preAuth.status === 'approved' && (
                <div className="mt-4 p-3 bg-light rounded">
                  <h6 className="text-success mb-3">Información de Aprobación</h6>
                  <div className="row g-2">
                    <div className="col-md-3">
                      <label className="text-muted small">Monto Aprobado</label>
                      <p className="fw-bold mb-0">{formatAmount(preAuth.approved_amount)}</p>
                    </div>
                    <div className="col-md-3">
                      <label className="text-muted small">Copago</label>
                      <p className="fw-medium mb-0">{formatAmount(preAuth.copay_amount)}</p>
                    </div>
                    <div className="col-md-3">
                      <label className="text-muted small">Deducible</label>
                      <p className="fw-medium mb-0">{formatAmount(preAuth.deductible_amount)}</p>
                    </div>
                    <div className="col-md-3">
                      <label className="text-muted small">% Cobertura</label>
                      <p className="fw-medium mb-0">{preAuth.coverage_percentage != null ? `${preAuth.coverage_percentage}%` : '-'}</p>
                    </div>
                    {preAuth.expiration_date && (
                      <div className="col-md-6">
                        <label className="text-muted small">Vence</label>
                        <p className="fw-medium mb-0">{formatDate(preAuth.expiration_date)}</p>
                      </div>
                    )}
                    {preAuth.authorizedBy && (
                      <div className="col-md-6">
                        <label className="text-muted small">Autorizado por</label>
                        <p className="fw-medium mb-0">{preAuth.authorizedBy.name}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Rejection info */}
              {preAuth.status === 'rejected' && preAuth.rejection_reason && (
                <div className="mt-4 p-3 bg-danger bg-opacity-10 rounded border border-danger">
                  <h6 className="text-danger mb-2">Motivo de Rechazo</h6>
                  <p className="mb-0">{preAuth.rejection_reason}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Actions sidebar */}
        <div className="col-md-4">
          <div className="card">
            <div className="card-body">
              <h6 className="card-title mb-3">Acciones</h6>
              <div className="d-grid gap-2">
                {preAuth.status === 'pending' && (
                  <>
                    <button className="btn btn-success" onClick={() => { setShowApproveModal(true); setActionError(null); }}>
                      <i className="fa fa-check me-1" /> Aprobar
                    </button>
                    <button className="btn btn-danger" onClick={() => { setShowRejectModal(true); setActionError(null); }}>
                      <i className="fa fa-times me-1" /> Rechazar
                    </button>
                    <button className="btn btn-outline-secondary" onClick={requestCancel} disabled={actionLoading}>
                      <i className="fa fa-ban me-1" /> Cancelar
                    </button>
                  </>
                )}
                {preAuth.status === 'approved' && (
                  <>
                    <button className="btn btn-primary" onClick={requestMarkUsed} disabled={actionLoading}>
                      <i className="fa fa-check-double me-1" /> Marcar como Usada
                    </button>
                    <button className="btn btn-outline-secondary" onClick={requestCancel} disabled={actionLoading}>
                      <i className="fa fa-ban me-1" /> Cancelar
                    </button>
                  </>
                )}
                {['rejected', 'expired', 'cancelled', 'used'].includes(preAuth.status) && (
                  <p className="text-muted small text-center mb-0">Sin acciones disponibles</p>
                )}
                <Link to="/pre-authorizations" className="btn btn-outline-primary mt-2">
                  <i className="fa fa-arrow-left me-1" /> Volver a la lista
                </Link>
                {preAuth.patient_id && (
                  <Link to={`/patients/${preAuth.patient_id}`} className="btn btn-outline-secondary">
                    <i className="fa fa-user me-1" /> Ver paciente
                  </Link>
                )}
              </div>
            </div>
          </div>

          {/* External ARS Code */}
          <div className="card mt-3">
            <div className="card-body">
              <h6 className="card-title mb-1">
                <i className="fa fa-id-card me-2 text-primary" />
                Código ARS
              </h6>
              <p className="text-muted small mb-3">
                Número recibido del portal o teléfono de la aseguradora.
              </p>
              {preAuth.external_auth_code && (
                <div className="alert alert-success py-2 px-3 mb-3 d-flex align-items-center gap-2">
                  <i className="fa fa-check-circle" />
                  <span className="fw-medium font-monospace">{preAuth.external_auth_code}</span>
                </div>
              )}
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Ej: ARS-2026-001234"
                  value={externalCode}
                  onChange={(e) => setExternalCode(e.target.value)}
                  maxLength={100}
                />
                <button
                  className={`btn ${codeSaved ? 'btn-success' : 'btn-primary'}`}
                  onClick={handleSaveExternalCode}
                  disabled={savingCode}
                >
                  {savingCode
                    ? <Spinner size="sm" />
                    : codeSaved
                      ? <i className="fa fa-check" />
                      : <i className="fa fa-save" />}
                </button>
              </div>
              {codeSaved && (
                <small className="text-success mt-1 d-block">
                  <i className="fa fa-check me-1" />Código guardado correctamente
                </small>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Approve Modal */}
      {showApproveModal && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Aprobar Pre-Autorización</h5>
                <button type="button" className="btn-close" onClick={() => setShowApproveModal(false)} />
              </div>
              <div className="modal-body">
                {actionError && <div className="alert alert-danger">{actionError}</div>}
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Monto Aprobado (RD$)</label>
                    <input type="number" className="form-control" min={0} step="0.01"
                      onChange={(e) => setApproveForm(f => ({ ...f, approved_amount: Number(e.target.value) }))} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Copago (RD$)</label>
                    <input type="number" className="form-control" min={0} step="0.01"
                      onChange={(e) => setApproveForm(f => ({ ...f, copay_amount: Number(e.target.value) }))} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Deducible (RD$)</label>
                    <input type="number" className="form-control" min={0} step="0.01"
                      onChange={(e) => setApproveForm(f => ({ ...f, deductible_amount: Number(e.target.value) }))} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">% Cobertura</label>
                    <input type="number" className="form-control" min={0} max={100}
                      onChange={(e) => setApproveForm(f => ({ ...f, coverage_percentage: Number(e.target.value) }))} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Fecha de Vencimiento</label>
                    <input type="date" className="form-control"
                      onChange={(e) => setApproveForm(f => ({ ...f, expiration_date: e.target.value }))} />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Notas</label>
                    <textarea className="form-control" rows={2}
                      onChange={(e) => setApproveForm(f => ({ ...f, notes: e.target.value }))} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowApproveModal(false)}>Cancelar</button>
                <button className="btn btn-success" onClick={handleApprove} disabled={actionLoading}>
                  {actionLoading ? <Spinner size="sm" /> : null} Confirmar Aprobación
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Rechazar Pre-Autorización</h5>
                <button type="button" className="btn-close" onClick={() => setShowRejectModal(false)} />
              </div>
              <div className="modal-body">
                {actionError && <div className="alert alert-danger">{actionError}</div>}
                <div className="mb-3">
                  <label className="form-label">Motivo de Rechazo <span className="text-danger">*</span></label>
                  <textarea className="form-control" rows={4} value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Indique el motivo por el cual la ARS rechaza la autorización..." />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowRejectModal(false)}>Cancelar</button>
                <button className="btn btn-danger" onClick={handleReject} disabled={actionLoading || !rejectReason.trim()}>
                  {actionLoading ? <Spinner size="sm" /> : null} Confirmar Rechazo
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        show={showCancelConfirm}
        title="Cancelar pre-autorización"
        body="¿Está seguro de cancelar esta pre-autorización? Esta acción no se puede deshacer."
        confirmLabel="Cancelar"
        confirmVariant="warning"
        loading={actionLoading}
        onConfirm={handleCancel}
        onCancel={cancelCancelRequest}
      />

      <ConfirmModal
        show={showMarkUsedConfirm}
        title="Marcar como usada"
        body="¿Marcar esta pre-autorización como usada?"
        confirmLabel="Marcar como usada"
        confirmVariant="primary"
        loading={actionLoading}
        onConfirm={handleMarkUsed}
        onCancel={cancelMarkUsedRequest}
      />
    </>
  );
};

export default PreAuthorizationDetailPage;
